import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Gavel, Clock, Eye, Shield, AlertTriangle, ChevronLeft, TrendingUp, User, CheckCircle, Truck, Package, XCircle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { BuyerCommitmentModal } from '../components/BuyerCommitmentModal';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'text-zinc-400 bg-zinc-500/10', icon: null },
  scheduled: { label: 'Programada', color: 'text-blue-300 bg-blue-500/10', icon: Clock },
  live: { label: 'En vivo', color: 'text-emerald-300 bg-emerald-500/10', icon: TrendingUp },
  ending_soon: { label: 'Por terminar', color: 'text-red-300 bg-red-500/10 animate-pulse', icon: AlertTriangle },
  awaiting_payment: { label: 'Esperando pago', color: 'text-amber-300 bg-amber-500/10', icon: Clock },
  paid: { label: 'Pagada', color: 'text-emerald-300 bg-emerald-500/10', icon: CheckCircle },
  shipped: { label: 'Enviada', color: 'text-blue-300 bg-blue-500/10', icon: Truck },
  completed: { label: 'Completada', color: 'text-emerald-300 bg-emerald-500/10', icon: CheckCircle },
  disputed: { label: 'En disputa', color: 'text-red-300 bg-red-500/10', icon: AlertTriangle },
  canceled: { label: 'Cancelada', color: 'text-zinc-400 bg-zinc-500/10', icon: XCircle },
  fraud_review: { label: 'En revisión', color: 'text-orange-300 bg-orange-500/10', icon: Shield },
};

function CountdownTimer({ endTime, label = 'Termina en' }) {
  const [timeLeft, setTimeLeft] = useState({});
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    const calc = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = end - now;
      if (diff <= 0) {
        setTimeLeft({ d: 0, h: 0, m: 0, s: 0, ended: true });
        return;
      }
      setIsUrgent(diff < 600000); // < 10 min
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        ended: false,
      });
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  if (timeLeft.ended) return <span className="text-red-400 font-mono text-lg">Terminada</span>;

  return (
    <div data-testid="countdown-timer">
      <span className="text-xs text-zinc-500 block mb-1">{label}</span>
      <div className={`flex gap-2 font-mono text-lg ${isUrgent ? 'text-red-400' : 'text-zinc-100'}`}>
        {timeLeft.d > 0 && (
          <div className="text-center">
            <span className="block text-xl font-bold">{timeLeft.d}</span>
            <span className="text-[10px] text-zinc-500 uppercase">Días</span>
          </div>
        )}
        <div className="text-center">
          <span className="block text-xl font-bold">{String(timeLeft.h).padStart(2, '0')}</span>
          <span className="text-[10px] text-zinc-500 uppercase">Hrs</span>
        </div>
        <span className="text-zinc-600 self-start text-xl">:</span>
        <div className="text-center">
          <span className="block text-xl font-bold">{String(timeLeft.m).padStart(2, '0')}</span>
          <span className="text-[10px] text-zinc-500 uppercase">Min</span>
        </div>
        <span className="text-zinc-600 self-start text-xl">:</span>
        <div className="text-center">
          <span className="block text-xl font-bold">{String(timeLeft.s).padStart(2, '0')}</span>
          <span className="text-[10px] text-zinc-500 uppercase">Seg</span>
        </div>
      </div>
    </div>
  );
}

export default function AuctionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [showCommitment, setShowCommitment] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  const loadAuction = useCallback(async () => {
    try {
      const data = await api.getAuction(id);
      setAuction(data);
      // Set min bid
      if (data.current_bid > 0) {
        setBidAmount(String(data.current_bid + (data.bid_increment_min || 50)));
      } else {
        setBidAmount(String(data.start_price));
      }
    } catch (e) {
      toast.error('Subasta no encontrada');
      navigate('/subastas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadAuction();
    // Poll every 10 seconds for live auctions
    const interval = setInterval(loadAuction, 10000);
    return () => clearInterval(interval);
  }, [loadAuction]);

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p);

  const handleBidClick = () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setShowCommitment(true);
  };

  const handleCommitmentConfirmed = async () => {
    setBidding(true);
    try {
      const fingerprint = navigator.userAgent + '|' + screen.width + 'x' + screen.height;
      const result = await api.placeBid(id, {
        amount: parseFloat(bidAmount),
        fingerprint: btoa(fingerprint).substring(0, 64),
      });
      toast.success('Puja registrada exitosamente');
      if (result.anti_sniping_triggered) {
        toast.info('Anti-sniping activado: la subasta se extendió');
      }
      loadAuction();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBidding(false);
    }
  };

  const handleWatch = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const result = await api.toggleWatchAuction(id);
      setAuction(prev => ({ ...prev, is_watching: result.watching, watcher_count: prev.watcher_count + (result.watching ? 1 : -1) }));
      toast.success(result.watching ? 'Siguiendo subasta' : 'Dejaste de seguir');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleAction = async (actionFn, successMsg) => {
    setActionLoading(true);
    try {
      await actionFn(id);
      toast.success(successMsg);
      loadAuction();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] pt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="aspect-square skeleton rounded-2xl" />
            <div className="space-y-4">
              <div className="h-8 w-3/4 skeleton rounded" />
              <div className="h-6 w-1/4 skeleton rounded" />
              <div className="h-48 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!auction) return null;

  const images = auction.images?.length > 0 ? auction.images : ['/placeholder-animal.jpg'];
  const statusCfg = STATUS_CONFIG[auction.status] || STATUS_CONFIG.live;
  const StatusIcon = statusCfg.icon;
  const isLive = ['live', 'ending_soon'].includes(auction.status);
  const isOwner = user?.id === auction.seller_id;
  const isWinner = user?.id === auction.winner_id;
  const minBid = auction.current_bid > 0 ? auction.current_bid + (auction.bid_increment_min || 50) : auction.start_price;

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="auction-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/subastas" className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Subastas
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-400 truncate">{auction.title}</span>
        </nav>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* LEFT — Images */}
          <div className="lg:col-span-3 space-y-4">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#121215]">
              <img src={images[currentImage]} alt={auction.title} className="w-full h-full object-cover" />
              {auction.is_demo && (
                <>
                  <span className="absolute top-4 right-4 z-10 bg-amber-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded tracking-wider uppercase backdrop-blur-sm" data-testid="auction-demo-badge">Demo</span>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
                    <span className="text-white/20 text-6xl font-black tracking-[0.3em] uppercase rotate-[-25deg] select-none">PRUEBA</span>
                  </div>
                </>
              )}
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold border backdrop-blur-md flex items-center gap-1.5 ${statusCfg.color}`}>
                  {StatusIcon && <StatusIcon className="w-3.5 h-3.5" />}
                  {statusCfg.label}
                </span>
              </div>
              {auction.anti_sniping_extensions_count > 0 && (
                <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 backdrop-blur-md">
                  Anti-sniping: +{auction.anti_sniping_extensions_count}
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button key={img} onClick={() => setCurrentImage(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${currentImage === i ? 'border-amber-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <div className="glass-card p-6 space-y-4">
              <h2 className="text-lg font-medium text-zinc-100">Descripción</h2>
              <p className="text-sm text-zinc-400 whitespace-pre-wrap">{auction.description}</p>
            </div>

            {/* Bid History */}
            <div className="glass-card p-6" data-testid="bid-history">
              <h2 className="text-lg font-medium text-zinc-100 mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-amber-400" />
                Historial de pujas ({auction.bid_count})
              </h2>
              {auction.bids?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {auction.bids.map((bid, i) => (
                    <div key={bid.id} className={`flex items-center justify-between p-3 rounded-lg ${i === 0 ? 'bg-amber-500/5 border border-amber-500/10' : 'bg-white/[0.02]'}`}
                      data-testid={`bid-entry-${bid.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <span className="text-sm text-zinc-200">{bid.user_name}</span>
                          {bid.risk_flagged && (
                            <span className="ml-2 text-[10px] text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded">riesgo</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${i === 0 ? 'text-amber-300' : 'text-zinc-300'}`}>
                          {formatPrice(bid.amount)}
                        </span>
                        <span className="block text-[10px] text-zinc-600">
                          {new Date(bid.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-600 text-center py-6">Aún no hay pujas. ¡Sé el primero!</p>
              )}
            </div>
          </div>

          {/* RIGHT — Bidding Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Price */}
            <div>
              <h1 className="text-2xl md:text-3xl font-medium text-zinc-100 mb-3 font-[Outfit]" data-testid="auction-title">
                {auction.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <span className="flex items-center gap-1"><Gavel className="w-4 h-4" /> {auction.bid_count} pujas</span>
                <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {auction.watcher_count} siguiendo</span>
              </div>
            </div>

            {/* Timer */}
            {isLive && (
              <div className="glass-card p-5 border-amber-500/10">
                <CountdownTimer endTime={auction.end_time} />
              </div>
            )}

            {auction.status === 'awaiting_payment' && auction.payment_deadline_at && (
              <div className="glass-card p-5 border-amber-500/10">
                <CountdownTimer endTime={auction.payment_deadline_at} label="Plazo de pago" />
              </div>
            )}

            {/* Current bid */}
            <div className="glass-card p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <span className="text-xs text-zinc-500 block mb-1">
                    {auction.current_bid > 0 ? 'Puja actual' : 'Precio inicial'}
                  </span>
                  <span className="text-3xl font-bold text-zinc-100 font-[Outfit]" data-testid="current-bid">
                    {formatPrice(auction.current_bid > 0 ? auction.current_bid : auction.start_price)}
                  </span>
                </div>
                {auction.reserve_price && auction.current_bid < auction.reserve_price && (
                  <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full border border-amber-500/20">
                    Reserva no alcanzada
                  </span>
                )}
              </div>

              {auction.winner_name && isLive && (
                <div className="text-xs text-zinc-500 mb-3">
                  Líder: <span className="text-zinc-300">{auction.winner_name}</span>
                </div>
              )}

              {/* Bid Form */}
              {isLive && !isOwner && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-500 block mb-1">
                      Tu puja (mínimo {formatPrice(minBid)})
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                      <input
                        type="number"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        min={minBid}
                        step={auction.bid_increment_min || 50}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-8 py-3 text-zinc-100 text-lg font-semibold focus:outline-none focus:border-amber-500/50 font-[Outfit]"
                        placeholder={String(minBid)}
                        data-testid="bid-amount-input"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">MXN</span>
                    </div>
                  </div>
                  <Button
                    onClick={handleBidClick}
                    disabled={bidding || parseFloat(bidAmount) < minBid}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3 text-base"
                    data-testid="place-bid-btn"
                  >
                    <Gavel className="w-5 h-5 mr-2" />
                    {bidding ? 'Registrando puja...' : 'Pujar'}
                  </Button>
                  {auction.user_highest_bid && (
                    <p className="text-xs text-zinc-500 text-center">
                      Tu mejor puja: {formatPrice(auction.user_highest_bid)}
                    </p>
                  )}
                </div>
              )}

              {/* Owner actions */}
              {isOwner && isLive && auction.bid_count === 0 && (
                <Button
                  variant="outline"
                  className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10"
                  onClick={() => handleAction(api.cancelAuction, 'Subasta cancelada')}
                  disabled={actionLoading}
                  data-testid="cancel-auction-btn"
                >
                  Cancelar subasta
                </Button>
              )}

              {/* Winner actions */}
              {isWinner && auction.status === 'awaiting_payment' && (
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => handleAction(api.confirmAuctionPayment, 'Pago confirmado')}
                  disabled={actionLoading}
                  data-testid="confirm-payment-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" /> Confirmar pago
                </Button>
              )}

              {/* Seller: confirm shipped */}
              {isOwner && auction.status === 'paid' && (
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white"
                  onClick={() => handleAction(api.confirmAuctionShipped, 'Envío confirmado')}
                  disabled={actionLoading}
                  data-testid="confirm-shipped-btn"
                >
                  <Truck className="w-5 h-5 mr-2" /> Confirmar envío
                </Button>
              )}

              {/* Buyer: confirm receipt */}
              {isWinner && auction.status === 'shipped' && (
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => handleAction(api.completeAuction, 'Recepción confirmada. ¡Subasta completada!')}
                  disabled={actionLoading}
                  data-testid="confirm-receipt-btn"
                >
                  <Package className="w-5 h-5 mr-2" /> Confirmar recepción
                </Button>
              )}
            </div>

            {/* Watch button */}
            <Button
              variant="outline"
              className={`w-full ${auction.is_watching ? 'border-amber-500/30 text-amber-400' : 'border-white/10 text-zinc-400'}`}
              onClick={handleWatch}
              data-testid="watch-auction-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {auction.is_watching ? 'Siguiendo' : 'Seguir subasta'}
            </Button>

            {/* Seller card */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-sm">
                  {auction.seller_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="text-sm font-medium text-zinc-200">{auction.seller_name}</span>
                  <span className="block text-xs text-zinc-500">Vendedor</span>
                </div>
              </div>
            </div>

            {/* Auction info */}
            <div className="glass-card p-5 space-y-3">
              <h3 className="text-sm font-medium text-zinc-300">Detalles de la subasta</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white/[0.02] p-3 rounded-lg">
                  <span className="text-zinc-500 block mb-1">Precio inicial</span>
                  <span className="text-zinc-200">{formatPrice(auction.start_price)}</span>
                </div>
                <div className="bg-white/[0.02] p-3 rounded-lg">
                  <span className="text-zinc-500 block mb-1">Incremento mín.</span>
                  <span className="text-zinc-200">{formatPrice(auction.bid_increment_min)}</span>
                </div>
                <div className="bg-white/[0.02] p-3 rounded-lg">
                  <span className="text-zinc-500 block mb-1">Anti-sniping</span>
                  <span className="text-zinc-200">+{auction.anti_sniping_extension_minutes}min</span>
                </div>
                <div className="bg-white/[0.02] p-3 rounded-lg">
                  <span className="text-zinc-500 block mb-1">Plazo pago</span>
                  <span className="text-zinc-200">{auction.payment_deadline_hours}h</span>
                </div>
              </div>
              {auction.fraud_risk_score > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-500/5 border border-orange-500/10 text-xs text-orange-400">
                  <Shield className="w-4 h-4" />
                  Risk score: {auction.fraud_risk_score}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Commitment Modal */}
      <BuyerCommitmentModal
        isOpen={showCommitment}
        onClose={() => setShowCommitment(false)}
        onConfirm={handleCommitmentConfirmed}
        actionType="bid"
        listingId={auction?.listing_id}
        auctionId={auction?.id}
      />
    </div>
  );
}
